/* eslint-disable complexity */
import { Command, RoomWebsocketErrorCode } from '@tencent/docs-connection';
import { RevisionEventTypes, SocketEventTypes } from '@tencent/slide-sdk-model';
import { network } from '@tencent/netcheck';
import { GetSlideAppErrorByCode, ServerErrorCode, SlideAppError } from './constants/error-code';
import { WaitAcceptCmd, RetrySource, ErrorSource } from './constants';
import { SubmitSendErrorHandler } from './handler/submit-send-error-handler';
import { SubmitAcceptHandler } from './handler/submit-accept-handler';
import { UserChangeManager, FetchSource } from './handler/fetch-missing-handler';
import { UserChange, type IUserChange, UserChangeStatus } from './utils/user-change';
import type {
    IDownstreamUserChange,
    IErrorMsg,
    IRevisionChangesetHandler,
    IRevisionConfig,
    IRevisionHandler,
    ISocketHandler,
} from './types';
import type { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import type { ICommandData } from '@tencent/slide-sdk-model';
import type { IEventManager } from '@tencent/slide-sdk-model/src/manager/event-manager';
import type { IServerMessage } from '@tencent/docs-connection';

const MODULE_NAME = '[SDK][Rev]';

export class Revision implements IRevisionHandler {
    private submitSendErrorHandler: SubmitSendErrorHandler;
    private submitAcceptHandler: SubmitAcceptHandler;
    private userChangeManager: UserChangeManager;

    // 当前本地最新版本号
    private revisionNumber: number;

    // 因为post错误的重试次数
    private retryPostCount = 0;

    // 因为 submit 错误的重试次数
    private retrySubmitCount = 0;

    private revisionConfig: IRevisionConfig;

    // 当前正在提交的变更
    private submittingUserChange: IUserChange | undefined;

    public constructor(
        private revisionChangesetHandler: IRevisionChangesetHandler,
        private socket: ISocketHandler,
        private event: IEventManager,
        public logger: ILogger,
    ) {
        this.submitSendErrorHandler = new SubmitSendErrorHandler(this, MODULE_NAME);
        this.submitAcceptHandler = new SubmitAcceptHandler(this, MODULE_NAME);
        this.userChangeManager = new UserChangeManager(this, MODULE_NAME);

        this.event.on(SocketEventTypes.NewChanges, this.handleNewChanges);
        this.event.on(SocketEventTypes.LatestVersion, this.updateLastRevision);
    }

    public init(config: IRevisionConfig) {
        this.revisionConfig = { ...config };
        this.revisionNumber = config.baseRev;
    }

    /**
     * 更新版本号，并将版本号应用到所有提交上
     * @param newRevisionNumber 版本号
     */
    public updateRevisionNumber = (newRevisionNumber: number) => {
        if (newRevisionNumber <= this.revisionNumber) {
            return;
        }
        this.logger.info(MODULE_NAME, `update base revision ${this.revisionNumber} to ${newRevisionNumber}`);
        this.revisionNumber = newRevisionNumber;
        this.socket.updateBaseRevision(newRevisionNumber);
        this.event.emit(RevisionEventTypes.UpdateBaseRevision, newRevisionNumber);
        this.userChangeManager.removeUserChangeBelowOrEqualVersion(newRevisionNumber);
        return true;
    };

    public getRevisionNumber = () => {
        return this.revisionNumber;
    };

    public getRetryPostCount = () => {
        return this.retryPostCount;
    };

    public updateRetryPostCount = (count: number) => {
        this.retryPostCount = count;
    };

    public getRetrySubmitCount = () => {
        return this.retrySubmitCount;
    };

    public updateRetrySubmitCount = (count: number) => {
        this.retrySubmitCount = count;
    };

    public getRevisionConfig = () => this.revisionConfig;

    public getSubmittingUserChange = () => this.submittingUserChange;

    /**
     * 统一处理后台的错误
     * @param error 错误信息
     */
    public dispatchError = async ({ taskId, error, code, message, shutDown, source }: IErrorMsg) => {
        const userChange = this.getSubmittingUserChange()!;
        // 重复提交不算错误，不知道最新版本号，直接走离线恢复 TODO 需要中台将错误码单独摘出，不要放到 600018 里面
        if (
            code === RoomWebsocketErrorCode.SOCKET_HACKPAD_TIMEOUT &&
            message?.includes(`${ServerErrorCode.COMMIT_DUPLICATE}`)
        ) {
            this.changeUserChangeStatus(userChange, UserChangeStatus.Accepted);
            try {
                const userChanges = await this.getMissingChanges(
                    userChange.revision + 1,
                    userChange.revision + 101,
                    FetchSource.Duplicate,
                );
                this.revisionChangesetHandler.handleNewUserChanges?.(userChanges);
            } catch (err) {
                return;
            }
            setTimeout(() => {
                // 重复提交，在本地队列里发现且同时不存在与协同队列里
                const shouldEmitError =
                    this.revisionChangesetHandler.getUserChangeFromLocalCache?.(taskId) &&
                    !this.revisionChangesetHandler.getUserChangeFromRemoteCache?.(taskId);
                if (shouldEmitError) {
                    this.event.emit(RevisionEventTypes.FetchMissingDataError, false, FetchSource.Duplicate);
                    this.logger.monitor?.reportStatus?.('duplicate', 1, '', [
                        this.revisionConfig.globalPadId,
                        `${network.isOnline}`,
                    ]);
                }
            }, 10000);
            return;
        }
        this.changeUserChangeStatus(userChange, UserChangeStatus.Error);
        this.logger.error(
            MODULE_NAME,
            `error: ${error}, code: ${code}, message: ${message}, shutdown: ${shutDown || false}`,
        );
        this.event.emit(RevisionEventTypes.CommitFail, {
            taskId,
            error,
            code,
            message,
            shutDown: !!shutDown,
            source,
        });
    };

    /**
     * 处理重复提交
     * @param taskId
     * @param errorCode
     */
    public handleDuplicateSubmit = (taskId: number, errorCode: number, source: ErrorSource) => {
        this.dispatchError({
            error: SlideAppError.ServerError,
            code: errorCode,
            taskId,
            message: `duplicate submit ${ServerErrorCode.COMMIT_DUPLICATE}`,
            source,
        });
    };

    /**
     * 处理提交版本号过低
     */
    public handleBaseVersionTooLow = async () => {
        this.event.emit(RevisionEventTypes.BaseVersionToLow);
        const expectedRev = this.revisionNumber + 200;
        this.changeUserChangeStatus(this.getSubmittingUserChange(), UserChangeStatus.BaseRevTooLow);
        this.getSubmittingUserChange()?.setExpectedRev(expectedRev);
        // 目前服务端是差200会提示版本过低，不排除以后会变化。
        await this.updateLastRevision(expectedRev);
    };

    /**
     * 处理致命错误
     * @param taskId
     * @param errorCode
     * @param message
     */
    public handleFatalError = (taskId: number, errorCode: number, message: string, source: ErrorSource) => {
        this.shutDownRevision({
            taskId,
            errCode: errorCode,
            msg: message ?? 'unknown fatal error',
            source,
        });
    };

    /**
     * 房间层推送最新的数据变更
     * @param serverMsg
     * @returns
     */
    public handleNewChanges = (serverMsg: IServerMessage) => {
        const { newRev, body } = serverMsg;
        if (!body.data) return;
        const { changeset: keyframe, commitId: taskId = 0 } = body.data;
        if (!keyframe) return;
        this.logger.info(MODULE_NAME, 'receive new changes,', taskId, 'rev:', newRev);
        const userChange: IDownstreamUserChange = {
            revision: newRev,
            keyframe,
            taskId,
        };
        this.userChangeManager.addUserChange(userChange);
        this.revisionChangesetHandler.handleNewUserChanges?.([userChange]);
    };

    /**
     * 后台落盘成功回调
     * @param taskId
     * @param baseRev
     * @param newRev
     * @param revisionData
     */
    public commitSuccess(taskId: number, baseRev: number, newRev: number, revisionData: string) {
        this.changeUserChangeStatus(this.getSubmittingUserChange(), UserChangeStatus.Accepted);
        this.userChangeManager.addUserChange({
            revision: newRev,
            keyframe: revisionData,
            taskId,
        });
        this.event.emit(RevisionEventTypes.CommitSucceed, { taskId, baseRev, newRev, revisionData });
    }

    /**
     * 发送文档变更
     * @param changeset 文档变更内容
     * @param complete 发送完成回调
     */
    public submitCommand = (commands: ICommandData[], taskId: number) => {
        if (taskId === this.submittingUserChange?.taskId) return;
        const userChange = this.createUserChange(commands, taskId);
        this.submitUserChange(userChange);
        this.event.emit(RevisionEventTypes.Enqueue, userChange);
    };

    public resetSubmittingUserChange = () => {
        this.submittingUserChange?.stopDetectIsBlocked();
        this.submittingUserChange = undefined;
    };

    /**
     * 获取断层数据
     * @param fromRev
     * @param toRev
     * @param fetchSource
     * @returns
     */
    public async getMissingChanges(fromRev: number, toRev: number, fetchSource: FetchSource) {
        let docIsReverted = false;
        try {
            const userChanges = (await this.userChangeManager.getUserChange(fromRev, toRev, fetchSource)).filter(
                (i) => !!i,
            );
            docIsReverted = !!userChanges.find((userChange) => !userChange.keyframe);
            if (docIsReverted) {
                throw new Error('doc is reverted');
            }
            return userChanges;
        } catch (err) {
            this.event.emit(RevisionEventTypes.FetchMissingDataError, docIsReverted, fetchSource);
            throw err;
        }
    }

    /**
     * 重试逻辑
     * 可能来自房间层错误码重试，也可能是本地探测超时重试
     * @param source
     * @param delayTime
     * @returns
     */
    public retrySubmit = async (source: RetrySource, delayTime = 3000) => {
        const submittingUserChange = this.getSubmittingUserChange();
        if (!submittingUserChange) return;
        this.changeUserChangeStatus(submittingUserChange, UserChangeStatus.Retrying);
        this.socket.cancelMessageByTaskId(submittingUserChange.taskId);

        if (delayTime >= 0) {
            this.logger.info(
                MODULE_NAME,
                `retry submit taskId ${submittingUserChange.taskId} after ${delayTime}ms, source: ${source}`,
            );
            await new Promise((resolve) => setTimeout(resolve, delayTime));
        }
        if (submittingUserChange.getStatus() !== UserChangeStatus.Retrying) {
            this.logger.info(
                MODULE_NAME,
                'no need to retry taskId',
                submittingUserChange.taskId,
                'status:',
                submittingUserChange.getStatus(),
            );
            return;
        }
        this.logger.info(
            MODULE_NAME,
            `retry submit taskId ${submittingUserChange.taskId} immediately, source: ${source}`,
        );
        this.submitUserChange(submittingUserChange);
    };

    /**
     * 切换房间层通信协议
     */
    public switchBigCollabMode = () => {
        this.socket.switchBigCollabMode();
    };

    private createUserChange(commands: ICommandData[], taskId: number) {
        const { uuId, subId, oSubId } = this.revisionConfig;
        return new UserChange({
            type: Command.USER_CHANGES,
            baseRev: this.revisionNumber,
            commands,
            keydata: false,
            head: false,
            uuId,
            subId,
            oSubId,
            pver: 1,
            taskId,
        });
    }

    private updateLastRevision = async (lastRev: number) => {
        if (this.revisionNumber < lastRev) {
            this.logger.info(MODULE_NAME, `update last revision to ${lastRev}, current is ${this.revisionNumber}`);
            const missingChanges = await this.getMissingChanges(
                this.revisionNumber + 1,
                lastRev,
                FetchSource.LatestVersion,
            );
            this.revisionChangesetHandler.handleNewUserChanges?.(missingChanges);
        }
    };

    /**
     * 向房间层提交变更数据
     * @param userChange
     */
    private submitUserChange = (userChange: IUserChange) => {
        this.logger.info(
            MODULE_NAME,
            'submit userChange, taskId:',
            userChange.taskId,
            'revision',
            userChange.revision,
            userChange.commands,
        );
        this.submittingUserChange = userChange;

        this.changeUserChangeStatus(this.getSubmittingUserChange(), UserChangeStatus.Sending);
        userChange.startDetectIsBlocked(() => {
            if (userChange.getBlockedRetryCount() > 1) {
                this.shutDownRevision({
                    taskId: userChange.taskId,
                    errCode: RoomWebsocketErrorCode.SOCKET_HACKPAD_TIMEOUT,
                    msg: 'userChange is blocked',
                    source: ErrorSource.Room,
                });
                return;
            }
            userChange.increaseBlockedRetryCount();
            this.retrySubmit(RetrySource.Block, -1);
        });
        this.socket.sendMessage({
            cmd: Command.USER_CHANGES,
            taskId: userChange.taskId,
            data: userChange.serialize(),
            customParam: {
                timeout: 25000,
                maxRetryCount: 3,
                waitAcceptCmd: [WaitAcceptCmd.AcceptCommit, WaitAcceptCmd.CommitError, WaitAcceptCmd.TaskDuplicate],
            },
            onError: this.submitSendErrorHandler.onSubmitError,
            onsend: this.submitSendErrorHandler.onSubmitSend,
            onsendAccept: this.submitAcceptHandler.onSubmitAccept,
        });
    };

    private shutDownRevision({
        taskId = -1,
        errCode = -1,
        msg,
        source,
    }: {
        taskId: number;
        errCode: number;
        msg: string;
        source: ErrorSource;
    }) {
        this.socket.close(`${errCode}`, errCode);
        this.dispatchError({
            taskId,
            error: GetSlideAppErrorByCode(errCode),
            code: errCode,
            shutDown: true,
            message: msg,
            source,
        });
    }

    /**
     * 修改当前 UserChange 状态，有状态变更代表当前未阻塞
     * @param userChange
     * @param status
     */
    private changeUserChangeStatus(userChange: IUserChange | undefined, status: UserChangeStatus) {
        userChange?.setStatus(status);
        userChange?.stopDetectIsBlocked();
    }
}
